// © 2026 KaraokeClubs. All rights reserved.

import type { Venue } from "@/lib/types";

export interface Coords {
  lat: number;
  lng: number;
}

/**
 * Great-circle distance between two lat/lng points, in kilometres (haversine).
 * Good enough for "how far is this venue from me" within a city.
 */
export function haversineKm(a: Coords, b: Coords): number {
  const R = 6371; // Earth radius, km
  const toRad = (d: number) => (d * Math.PI) / 180;
  const dLat = toRad(b.lat - a.lat);
  const dLng = toRad(b.lng - a.lng);
  const lat1 = toRad(a.lat);
  const lat2 = toRad(b.lat);
  const h =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLng / 2) ** 2;
  return R * 2 * Math.asin(Math.min(1, Math.sqrt(h)));
}

/** Distance from the user to a venue in km, or null if either point is missing. */
export function venueDistanceKm(venue: Venue, from: Coords | null): number | null {
  if (!from || venue.latitude == null || venue.longitude == null) return null;
  return haversineKm(from, { lat: venue.latitude, lng: venue.longitude });
}

/** Human-friendly distance: "120 m", "1.2 km", "8 km". */
export function formatDistance(km: number | null): string | null {
  if (km == null || !isFinite(km)) return null;
  if (km < 1) return `${Math.round(km * 1000)} m`;
  if (km < 10) return `${km.toFixed(1)} km`;
  return `${Math.round(km)} km`;
}

/**
 * Sort venues by distance from the user (nearest first). Venues without
 * coordinates fall to the end. Pure — returns a new array.
 */
export function sortByDistance(venues: Venue[], from: Coords | null): Venue[] {
  if (!from) return [...venues];
  return [...venues].sort((a, b) => {
    const da = venueDistanceKm(a, from);
    const db = venueDistanceKm(b, from);
    if (da == null && db == null) return a.name.localeCompare(b.name);
    if (da == null) return 1;
    if (db == null) return -1;
    return da - db;
  });
}

/**
 * "Near me", but open venues always come first. Buckets venues by whether
 * karaoke is live right now (open / closing soon), then sorts each bucket by
 * distance. So the nearest OPEN place ranks above a closer closed one — the
 * user wants somewhere they can actually go tonight, sorted by how close it is.
 */
export function sortByDistanceOpenFirst(
  venues: Venue[],
  from: Coords | null,
  isOpen: (v: Venue) => boolean,
): Venue[] {
  const open: Venue[] = [];
  const rest: Venue[] = [];
  for (const v of venues) (isOpen(v) ? open : rest).push(v);
  return [...sortByDistance(open, from), ...sortByDistance(rest, from)];
}

export type TravelMode = "driving" | "walking" | "transit" | "bicycling";

/**
 * Build a Google Maps directions deep-link. When we know the user's location we
 * pass it as the origin and pick a travel mode, so the link opens turn-by-turn
 * navigation instead of just dropping a pin on the venue.
 */
export function buildDirectionsUrl(
  venue: Venue,
  from: Coords | null,
  mode: TravelMode = "transit",
): string {
  const dest =
    venue.latitude != null && venue.longitude != null
      ? `${venue.latitude},${venue.longitude}`
      : encodeURIComponent(`${venue.name} ${venue.address ?? ""}`.trim());
  const params = new URLSearchParams({ api: "1", destination: dest, travelmode: mode });
  if (from) params.set("origin", `${from.lat},${from.lng}`);
  return `https://www.google.com/maps/dir/?${params.toString()}`;
}
